import { useState, useEffect } from "react";
import { Mail, Calendar, Users, Send, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReminderManager = () => {
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [selectedDays, setSelectedDays] = useState("3");
  
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchUpcomingReminders();
  }, []);

  const fetchUpcomingReminders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reminders/upcoming`, {
        headers: getAuthHeader()
      });
      setUpcomingReminders(response.data.upcoming_sessions || response.data.upcoming_reminders || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
      toast.error("Failed to load upcoming reminders");
    } finally {
      setLoading(false);
    }
  };

  const sendReminders = async (daysBefore) => {
    setSending(true);
    try {
      const response = await axios.post(`${API}/reminders/send?days_before=${daysBefore}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`${response.data.reminders_queued} reminder emails queued successfully`);
      fetchUpcomingReminders();
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  const sendTestReminder = async (scheduleId) => {
    if (!testEmail) {
      toast.error("Please enter a test email address");
      return;
    }
    
    try {
      await axios.post(`${API}/reminders/test/${scheduleId}?test_email=${encodeURIComponent(testEmail)}`, {}, {
        headers: getAuthHeader()
      });
      toast.success(`Test reminder sent to ${testEmail}`);
    } catch (error) {
      toast.error("Failed to send test reminder");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => (
          <div key={i} className="h-32 skeleton rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold text-lg">Session Reminders</h3>
          <p className="text-sm text-muted-foreground">Send email reminders to enrolled participants</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedDays} onValueChange={setSelectedDays}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Days before" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1 day before</SelectItem>
              <SelectItem value="3">3 days before</SelectItem>
              <SelectItem value="7">7 days before</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            onClick={() => sendReminders(parseInt(selectedDays))} 
            disabled={sending}
            className="bg-brand hover:bg-brand-dark text-white"
            data-testid="send-reminders-btn"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Sending..." : "Send Reminders"}
          </Button>
        </div>
      </div>

      {/* Test Email Section */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <Input
              type="email"
              placeholder="Enter test email address"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="max-w-xs"
              data-testid="test-email-input"
            />
            <span className="text-sm text-muted-foreground">
              Use to preview reminder emails before sending to participants
            </span>
          </div>
        </CardContent>
      </Card>

      {upcomingReminders.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h4 className="font-medium mb-2">No upcoming sessions</h4>
            <p className="text-sm text-muted-foreground">
              Sessions scheduled within the next 7 days will appear here
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {upcomingReminders.map((group) => (
            <Card key={group.date} data-testid={`reminder-group-${group.days_until}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-brand" />
                    {formatDate(group.date)}
                    <Badge 
                      variant="outline" 
                      className={
                        group.days_until === 1 ? "bg-red-50 text-red-700 border-red-200" :
                        group.days_until === 3 ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                        "bg-blue-50 text-blue-700 border-blue-200"
                      }
                    >
                      {group.days_until === 1 ? "Tomorrow" : `In ${group.days_until} days`}
                    </Badge>
                  </CardTitle>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => sendReminders(group.days_until)}
                    disabled={sending}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Send for this date
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {group.schedules.map((schedule) => (
                    <div 
                      key={schedule.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="font-medium">{schedule.course_title}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Clock className="h-4 w-4" />
                            {schedule.start_time} - {schedule.end_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="h-4 w-4" />
                            {schedule.enrolled_participants || 0} enrolled
                          </span>
                          {schedule.instructor_name && (
                            <span>Instructor: {schedule.instructor_name}</span>
                          )}
                        </div>
                      </div>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => sendTestReminder(schedule.id)}
                        disabled={!testEmail}
                        className="text-brand hover:text-brand-dark"
                      >
                        <Mail className="h-4 w-4 mr-1" />
                        Send Test
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Box */}
      <Card className="mt-6 border-blue-200 bg-blue-50">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium mb-1">Automatic Reminder System</p>
              <p className="text-blue-700">
                For fully automated reminders, consider setting up a cron job or scheduled task 
                to call the <code className="bg-blue-100 px-1 rounded">/api/reminders/send</code> endpoint daily.
                This will automatically send reminders to all enrolled participants.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReminderManager;
